// # DO NOT EDIT THIS FILE
// # edit only shared/utils/ctimeUTC.py
// # after that use "make shared_utils" to copy to other locations

import { logger } from "@/lib/logger";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

const APP_TIMEZONE = "UTC";

export function toAppDate(timestamp: number | string | Date | null | undefined): Date | null {
  if (!timestamp) return null;

  try {
    if (timestamp instanceof Date) {
      return timestamp;
    }

    if (typeof timestamp === "string") {
      if (/^\d+$/.test(timestamp)) {
        const num = Number(timestamp);
        return toAppDate(num);
      }
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    if (typeof timestamp === "number") {
      const timestampLength = Math.floor(Math.abs(timestamp)).toString().length;
      const msTimestamp = timestampLength <= 10 ? timestamp * 1000 : timestamp;
      return new Date(msTimestamp);
    }

    return null;
  } catch (error) {
    logger.error("Error converting timestamp to Date:", error);
    return null;
  }
}

export function toUnixTimestamp(date: Date | null | undefined): number | null {
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
}

export function toISOLocalString(date: Date | null | undefined): string | null {
  if (!date) return null;

  try {
    return dayjs(date).tz(APP_TIMEZONE).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
  } catch (error) {
    logger.error("Error formatting date to ISO string:", error);
    return null;
  }
}

export function formatForDisplay(date: Date | null | undefined, formatStr = "YYYY-MM-DD HH:mm:ss"): string {
  if (!date) return "";

  try {
    return dayjs(date).tz(APP_TIMEZONE).format(formatStr);
  } catch (error) {
    logger.error("Error formatting date for display:", error);
    return "Invalid date";
  }
}

export function getCurrentUnixTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

export function getCurrentUnixTimestampMs(): number {
  return Date.now();
}

export function isMsTimestamp(timestamp: number): boolean {
  return timestamp > 1000000000000;
}

export function getCurrentAppDate(): Date {
  return new Date();
}

function getBrowserTimeZone(): string {
  const isServer = typeof window === "undefined";

  if (isServer) {
    logger.debug("getBrowserTimeZone: Server-side");
    return "UTC";
  }
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || Intl.DateTimeFormat().resolvedOptions().timeZoneName || "UTC"
    );
  } catch (e) {
    return "UTC";
  }
}

export function formatInLocalTimezone(
  timestamp: number | Date | null | undefined,
  formatStr = "YYYY-MM-DD HH:mm:ss"
): string {
  if (!timestamp) return "";

  try {
    const date = timestamp instanceof Date ? timestamp : toAppDate(timestamp);
    if (!date) return "Invalid date";

    const browserTimeZone = getBrowserTimeZone();
    return dayjs(date).tz(browserTimeZone).format(formatStr);
  } catch (error) {
    logger.error("Error formatting date in local timezone:", error);
    return "Invalid date";
  }
}

export const fromTimestampUTC = (time: number): Date => toAppDate(time) || new Date();
export const toJSDate = (timestamp: number | string | Date): Date => toAppDate(timestamp) || new Date();
export const toJSDateToLocalString = (timestamp: number | string | Date): string =>
  formatForDisplay(toAppDate(timestamp));

export const getIsoformatDatetime = (date: Date | null | undefined): string => {
  let date_to_isoformat = date;
  if (!date_to_isoformat) {
    date_to_isoformat = new Date();
  }
  return date_to_isoformat.toISOString();
};
